import React, { useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { useCore } from '../../hooks/useCore';
import { useData } from '../../hooks/useData';
import { ChevronDown, ChevronUp, FileText, FileSpreadsheet, FileCode, X, Upload, Clock, GitBranchPlus } from 'lucide-react';
import { ellipseText, getTimeDisplay } from '../../utils/tools';
import { useNavigate } from 'react-router-dom';
import { brandColors } from '../../utils/data';
import {
  ActiveFiltersCount,
  ApplyFiltersButton,
  Badge,
  BulkRepriceButton,
  BulkRepriceProgress,
  ClearFiltersButton,
  DateCell,
  DateInput,
  DateRangeContainer,
  FileIcon,
  FileName,
  FilterBar,
  FilterGroup,
  FilterLabel,
  PageSizeSelect,
  PageSizeSelectWrapper,
  PaginationButton,
  PaginationContainer,
  PaginationControls,
  PaginationInfo,
  SearchContainer,
  SearchIcon,
  SearchInput,
  SortIcon,
  StyledX12Logo,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableScrollContainer,
  TableWrapper,
  Td,
  Th,
  UserAvatar,
  UserCell,
  UserEmail,
  UserInfo,
  UserName,
} from '../../utils/filterTableStyles';
// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} bytes`;
  } else if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}kb`;
  } else {
    return `${Math.round(bytes / (1024 * 1024))}mb`;
  }
};

const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'xls':
    case 'xlsx':
      return <FileSpreadsheet size={20} color="#217346" />;
    case 'csv':
      return <FileText size={20} color="#FF6B35" />;
    case 'json':
      return <FileCode size={20} color="#F7DF1E" />;
    case 'x12':
      return <StyledX12Logo />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <FileText size={20} color="#FF6B35" />;
    default:
      return <FileText size={20} />;
  }
};

// Helper function to get file extension
const getFileExtension = (fileName) => {
  return fileName.split('.').pop()?.toLowerCase() || '';
};

// Helper function to format user name
const formatUserName = (user) => {
  if (!user) return 'Unknown';
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  return `${firstName} ${lastName}`.trim() || user.username || 'Unknown';
};

// Helper function to get user initials for avatar
const getUserInitials = (user) => {
  if (!user) return '?';
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const username = user.username || '';
  
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  } else if (firstName) {
    return firstName[0].toUpperCase();
  } else if (lastName) {
    return lastName[0].toUpperCase();
  } else if (username) {
    return username[0].toUpperCase();
  }
  return '?';
};

const getFileDateValue = (item) => {
  return item.uploaded || item.created || item.updated || null;
};

const getCommittedDateValue = (value) => {
  if (!value) {
    return '';
  }

  return value.length === 10 ? value : null;
};

const getClaimSearchValues = (claim) => {
  if (!claim) {
    return [];
  }

  const subscriberName = [
    claim.loops?.subscriber?.namePrefix,
    claim.loops?.subscriber?.firstName,
    claim.loops?.subscriber?.middleName,
    claim.loops?.subscriber?.name,
    claim.loops?.subscriber?.nameSuffix,
  ].filter(Boolean).join(' ');

  const patientName = [
    claim.loops?.patient?.namePrefix,
    claim.loops?.patient?.firstName,
    claim.loops?.patient?.middleName,
    claim.loops?.patient?.name,
    claim.loops?.patient?.nameSuffix,
  ].filter(Boolean).join(' ');

  return [
    subscriberName,
    patientName,
    claim.loops?.subscriber?.payer?.name,
    claim.loops?.billingProvider?.provider?.name,
    claim.claimInfo?.totalAmount != null ? String(claim.claimInfo.totalAmount) : '',
    claim.loops?.header?.submitter?.name,
  ].filter(Boolean);
};

const fileMatchesClaimSearch = (item, searchTerm) => {
  if (!searchTerm) {
    return true;
  }

  const claims = item?.data?.file?.claims || [];
  return claims.some((claim) =>
    getClaimSearchValues(claim).some((value) =>
      value.toLowerCase().includes(searchTerm)
    )
  );
};

const getMatchingClaimCount = (claims, searchTerm) => {
  if (!Array.isArray(claims)) {
    return 0;
  }

  if (!searchTerm) {
    return claims.length;
  }

  return claims.filter((claim) =>
    getClaimSearchValues(claim).some((value) =>
      value.toLowerCase().includes(searchTerm)
    )
  ).length;
};

const FilterTable = ({ data, showFilters = true, tableHeight, onFilteredDataChange }) => {
  const { globalFilter, handleGlobalFilter, theme } = useCore();
  const {
    files,
    file,
    openFile,
    handleFilteredFiles,
    appliedClaimSearchTerm,
    handleAppliedClaimSearchTerm,
    searchDraft,
    handleSearchDraft,
    dateRangeFilter,
    handleDateRangeFilter,
    dateRangeDraft,
    handleDateRangeDraft,
    processBulkReprice,
    bulkRepriceLoading,
  } = useData();
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  
  const columnHelper = createColumnHelper();

  // Use provided data or fall back to files from useData
  const tableData = data || files;

  // Apply the same filtering logic and expose filtered data
  const filteredData = useMemo(() => {
    let filtered = tableData;

    

    // Date range filter
    if (dateRangeFilter.from || dateRangeFilter.to) {
      filtered = filtered.filter(item => {
        const itemDateValue = getFileDateValue(item);
        if (!itemDateValue) {
          return false;
        }

        const date = new Date(itemDateValue);
        if (Number.isNaN(date.getTime())) {
          return false;
        }

        const from = dateRangeFilter.from ? new Date(`${dateRangeFilter.from}T00:00:00`) : null;
        const to = dateRangeFilter.to ? new Date(`${dateRangeFilter.to}T23:59:59.999`) : null;
        
        if (from && to) {
          return date >= from && date <= to;
        } else if (from) {
          return date >= from;
        } else if (to) {
          return date <= to;
        }
        return true;
      });
    }

    if (appliedClaimSearchTerm) {
      filtered = filtered.filter((item) => fileMatchesClaimSearch(item, appliedClaimSearchTerm.toLowerCase()));
    }

    // Global filter
    if (globalFilter) {
      const searchTerm = globalFilter.toLowerCase();
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(searchTerm) ||
        item.provider?.toLowerCase().includes(searchTerm) ||
        item.status?.toLowerCase().includes(searchTerm) ||
        item.location?.toLowerCase().includes(searchTerm) ||
        item.zipcode?.toLowerCase().includes(searchTerm) ||
        formatUserName(item.creator)?.toLowerCase().includes(searchTerm) ||
        formatUserName(item.updater)?.toLowerCase().includes(searchTerm) ||
        (item.creator?.username || '').toLowerCase().includes(searchTerm) ||
        (item.updater?.username || '').toLowerCase().includes(searchTerm) ||
        (item.codes && Array.isArray(item.codes) && item.codes.some(code => 
          code?.toLowerCase().includes(searchTerm)
        )) ||
        (item.notes && Array.isArray(item.notes) && item.notes.some(note => 
          note?.toLowerCase().includes(searchTerm)
        ))
      );
    }

    
    return filtered;
  }, [tableData, dateRangeFilter, globalFilter, appliedClaimSearchTerm]);

  // Update filtered files in DataProvider when filtered data changes
  useEffect(() => {
    handleFilteredFiles(filteredData);
  }, [filteredData, handleFilteredFiles]);

  // Notify parent component when filtered data changes
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredData);
    }
  }, [filteredData, onFilteredDataChange]);

  // Handle row click to navigate to file detail
  const handleRowClick = (file) => {
    openFile(file);
 
    setTimeout(() => {
      navigate(`/file/${file.slug}`);
    }, 100);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'File',
        cell: (info) => {
          const fileName = info.getValue();
          const fileExtension = getFileExtension(fileName);
          return (
            <FileIcon>
              {getFileIcon(fileName)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <FileName>{ellipseText(fileName, 39, true)}</FileName>
                {/* <FileType>{fileExtension.toUpperCase()}</FileType> */}
              </div>
            </FileIcon>
          );
        },
        enableSorting: true,
        minSize: 270,
        maxSize: 300,
      }),
      columnHelper.accessor('uploaded', {
        header: () => <Upload size={16} title="Uploaded" />, 
        cell: (info) => (
          <DateCell>
            <Clock style={{marginRight: '0.33rem'}} size={11} title="Uploaded" />
            <p style={{margin: 0,paddingTop: 1,fontSize: 'inherit'}}>{getTimeDisplay(info.getValue())}</p> 
          </DateCell>
        ),
        enableSorting: true,
        minSize: 140,
        maxSize: 160,
      }),
      columnHelper.accessor('provider', {
        header: 'Provider',
        cell: (info) => (
          <DateCell>
            {info.getValue() || 'N/A'}
          </DateCell>
        ),
        enableSorting: true,
        minSize: 120,
        maxSize: 150,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => (
          <Badge status={info.getValue()}>
            {info.getValue()}
          </Badge>
        ),
        enableSorting: true,
        minSize: 100,
        maxSize: 120,
      }),
      columnHelper.accessor('data.file.claims', {
        header: 'Claims',
        cell: (info) => {
          const claims = info.getValue();
          const matchingClaimsCount = getMatchingClaimCount(
            claims,
            appliedClaimSearchTerm ? appliedClaimSearchTerm.toLowerCase() : ''
          );

          return (
            <Badge>
              {matchingClaimsCount > 0 ? `${matchingClaimsCount} claim(s)` : 'No claims'}
            </Badge>
          );
        },
        enableSorting: true,
        minSize: 120,
        maxSize: 140,
      }),
      columnHelper.accessor('versions', {
        header: 'Versions',
        cell: (info) => {
          const versions = info.getValue();
          return (
            <Badge>
              {versions && versions.length > 0 ? `${versions.length} version(s)` : 'No versions'}
            </Badge>
          );
        },
        enableSorting: true,
        minSize: 120,
        maxSize: 140,
      }),
      
      // columnHelper.accessor('codes', {
      //   header: 'Codes',
      //   cell: (info) => {
      //     const codes = info.getValue();
      //     return (
      //       <Badge>
      //         {codes && codes.length > 0 ? `${codes.length} code(s)` : 'No codes'}
      //       </Badge>
      //     );
      //   },
      //   enableSorting: true,
      //   minSize: 120,
      //   maxSize: 140,
      // }),
      columnHelper.accessor('notes', {
        header: 'Notes',
        cell: (info) => {
          const notes = info.getValue();
          return (
            <Badge>
              {notes && notes.length > 0 ? `${notes.length} note(s)` : 'No notes'}
            </Badge>
          );
        },
        enableSorting: true,
        minSize: 120,
        maxSize: 140,
      }),
      columnHelper.accessor('location', {
        header: 'Location',
        cell: (info) => (
          <DateCell>
            {info.getValue() || 'N/A'}
          </DateCell>
        ),
        enableSorting: true,
        minSize: 120,
        maxSize: 150,
      }),
      // columnHelper.accessor('zipcode', {
      //   header: 'Zipcode',
      //   cell: (info) => (
      //     <DateCell>
      //       {info.getValue() || 'N/A'}
      //     </DateCell>
      //   ),
      //   enableSorting: true,
      //   minSize: 80,
      //   maxSize: 100,
      // }),
      columnHelper.accessor('updated', {
        header: 'Updated',
        cell: (info) => (
          <DateCell>
            <Clock style={{marginRight: '0.33rem'}} size={11} title="Uploaded" />
            <p style={{margin: 0,paddingTop: 1,fontSize: 'inherit'}}>{getTimeDisplay(info.getValue())}</p> 
          </DateCell>
        ),
        enableSorting: true,
        minSize: 140,
        maxSize: 160,
      }),
      
      columnHelper.accessor('updater', {
        header: 'Updated By',
        cell: (info) => {
          const user = info.getValue();
          return (
            <UserCell>
              <UserAvatar>{getUserInitials(user)}</UserAvatar>
              <UserInfo>
                <UserName>{formatUserName(user)}</UserName>
                <UserEmail>{user?.username || 'Unknown'}</UserEmail>
              </UserInfo>
            </UserCell>
          );
        },
        enableSorting: true,
        minSize: 120,
        maxSize: 120,
      }),

      
      

      columnHelper.accessor('creator', {
        header: 'Uploaded By',
        cell: (info) => {
          const user = info.getValue();
          return (
            <UserCell>
              <UserAvatar>{getUserInitials(user)}</UserAvatar>
              <UserInfo>
                <UserName>{formatUserName(user)}</UserName>
                <UserEmail>{user?.username || 'Unknown'}</UserEmail>
              </UserInfo>
            </UserCell>
          );
        },
        enableSorting: true,
        minSize: 120,
        maxSize: 120,
      }),
      
      columnHelper.accessor('created', {
        header: 'Last Modified',
        cell: (info) => (
          <DateCell>
            <Clock style={{marginRight: '0.33rem'}} size={11} title="Uploaded" />
            <p style={{margin: 0,paddingTop: 1,fontSize: 'inherit'}}>{getTimeDisplay(info.getValue())}</p> 
          </DateCell>
        ),
        enableSorting: true,
        minSize: 140,
        maxSize: 160,
      }),
      columnHelper.accessor('size', {
        header: 'Size',
        cell: (info) => (
          <DateCell>
            {formatFileSize(info.getValue())}
          </DateCell>
        ),
        enableSorting: true,
        minSize: 80,
        maxSize: 100,
      }),
    ],
    [columnHelper, appliedClaimSearchTerm]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
  });

  const activeFiltersCount = (appliedClaimSearchTerm ? 1 : 0) + 
                           ((dateRangeFilter.from || dateRangeFilter.to) ? 1 : 0);

  const applyFilters = () => {
    const nextFrom = getCommittedDateValue(dateRangeDraft.from);
    const nextTo = getCommittedDateValue(dateRangeDraft.to);

    if (nextFrom === null || nextTo === null) {
      return;
    }

    handleDateRangeFilter({
      from: nextFrom,
      to: nextTo,
    });
    handleAppliedClaimSearchTerm(searchDraft.trim());
  };

  const clearAllFilters = () => {
    handleDateRangeFilter({ from: '', to: '' });
    handleDateRangeDraft({ from: '', to: '' });
    handleSearchDraft('');
    handleAppliedClaimSearchTerm('');
    handleGlobalFilter('');
  };

  return (
    <TableContainer tableHeight={tableHeight}>
      {showFilters && (
        <FilterBar>
          <FilterGroup>
            <FilterLabel>Search Files</FilterLabel>
            <SearchContainer>
              <SearchInput
                value={searchDraft}
                onChange={(e) => handleSearchDraft(e.target.value)}
                placeholder="Subscriber | Patient, Payer, Provider, Total Amount, Submitter"
                style={{ width: '420px' }}
              />
              <SearchIcon size={16} />
            </SearchContainer>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date Range</FilterLabel>
            <DateRangeContainer>
              <DateInput
                type="date"
                value={dateRangeDraft.from}
                onChange={(e) => handleDateRangeDraft({ ...dateRangeDraft, from: e.target.value })}
                placeholder="From"
                max={dateRangeDraft.to || today}
                theme={theme}
              />
              <span style={{ color: 'var(--text-muted)' }}>to</span>
              <DateInput
                type="date"
                value={dateRangeDraft.to}
                onChange={(e) => handleDateRangeDraft({ ...dateRangeDraft, to: e.target.value })}
                placeholder="To"
                min={dateRangeDraft.from || undefined}
                max={today}
                theme={theme}
              />
              <ApplyFiltersButton type="button" onClick={applyFilters}>
                Apply
              </ApplyFiltersButton>
            </DateRangeContainer>
          </FilterGroup>

          <BulkRepriceButton
            type="button"
            onClick={processBulkReprice}
            disabled={bulkRepriceLoading}
          >
            {bulkRepriceLoading ? (
              <BulkRepriceProgress aria-label="Bulk reprice in progress" />
            ) : (
              <GitBranchPlus size={14} />
            )}
            {bulkRepriceLoading ? 'Processing' : 'Bulk reprice'}
          </BulkRepriceButton>

          {activeFiltersCount > 0 && (
            <ClearFiltersButton onClick={clearAllFilters}>
              <X size={14} />
              Clear Filters
              <ActiveFiltersCount>{activeFiltersCount}</ActiveFiltersCount>
            </ClearFiltersButton>
          )}
        </FilterBar>
      )}

      <TableWrapper>
        <TableScrollContainer>
          <Table key={`table-${filteredData.length}`}>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      sortable={header.column.getCanSort()}
                      onClick={header.column.getToggleSortingHandler()}
                      minWidth={`${header.column.columnDef.minSize}px`}
                      maxWidth={`${header.column.columnDef.maxSize}px`}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      <SortIcon>
                        {{
                          asc: <ChevronUp color={brandColors['blue']} size={16} />,
                          desc: <ChevronDown color={brandColors['blue']} size={16} />,
                        }[header.column.getIsSorted()] ?? null}
                      </SortIcon>
                    </Th>
                  ))}
                </tr>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} onClick={() => handleRowClick(row.original)}>
                  {row.getVisibleCells().map((cell) => (
                    <Td 
                      key={cell.id}
                      minWidth={`${cell.column.columnDef.minSize}px`}
                      maxWidth={`${cell.column.columnDef.maxSize}px`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </tr>
              ))}
            </TableBody>
          </Table>
        </TableScrollContainer>
      </TableWrapper>

      <PaginationContainer>
        <PaginationInfo>
          Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
          {Math.min(
            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
            table.getFilteredRowModel().rows.length
          )}{' '}
          of {table.getFilteredRowModel().rows.length} results
        </PaginationInfo>
        <PaginationControls>
          <PaginationButton
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </PaginationButton>
          <PaginationButton
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </PaginationButton>
          <PageSizeSelectWrapper>
            <PageSizeSelect
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </PageSizeSelect>
            <ChevronDown size={16} />
          </PageSizeSelectWrapper>
        </PaginationControls>
      </PaginationContainer>
    </TableContainer>
  );
};

export default FilterTable; 
